import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, DataTable, Input } from '../../../../components/ui';
import { getUsers, userKeys } from '../../../../api/user.api';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';
import { useDebounce } from '../../../../hooks/useDebounce';
import type { IamUser } from '../../../../types/iam-user.types';

const columns: ColumnsType<IamUser> = [
	{
		title: 'First Name',
		dataIndex: 'firstName',
		key: 'firstName',
		width: 180,
		sorter: (a, b) => a.firstName.localeCompare(b.firstName),
	},
	{
		title: 'Last Name',
		dataIndex: 'lastName',
		key: 'lastName',
		width: 180,
		sorter: (a, b) => a.lastName.localeCompare(b.lastName),
	},
	{
		title: 'Username',
		dataIndex: 'userName',
		key: 'userName',
		width: 160,
	},
	{
		title: 'Email',
		dataIndex: 'email',
		key: 'email',
		width: 220,
	},
	{
		title: 'Roles',
		dataIndex: 'roles',
		key: 'roles',
		width: 200,
		render: (_: unknown, record: IamUser) =>
			record.roles?.map((r) => (
				<Tag key={r.value} color="blue">
					{r.label}
				</Tag>
			)),
	},
	{
		title: 'Status',
		dataIndex: 'status',
		key: 'status',
		width: 120,
		align: 'center',
		render: (_: unknown, record: IamUser) => {
			const isActive = record.status === 'USER_STATUS_ACTIVE';
			return (
				<Tag color={isActive ? 'green' : 'default'}>
					{isActive ? 'Active' : 'Inactive'}
				</Tag>
			);
		},
	},
];

interface UserListViewProps {
	createPermission?: string;
}

export default function UserListView({ createPermission = 'iam:user:create' }: UserListViewProps) {
	const navigate = useNavigate();
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounce(search, 400);

	const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useInfiniteScroll<IamUser>({
			queryKey: [...userKeys.lists(), { search: debouncedSearch }],
			queryFn: getUsers,
			search: debouncedSearch || undefined,
		});

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<p className="text-sm text-gray-500 m-0">Manage user accounts and permissions.</p>
				<Button
					variant="primary"
					icon={<PlusOutlined />}
					permission={createPermission}
					onClick={() => navigate('/iam/users/create')}
				>
					Create User
				</Button>
			</div>

			<div className="mb-4 max-w-sm">
				<Input
					placeholder="Search users..."
					prefix={<SearchOutlined />}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					allowClear
				/>
			</div>

			<DataTable<IamUser>
				columns={columns}
				dataSource={items}
				rowKey="id"
				loading={isLoading}
				onRowClick={(record) => navigate(`/iam/users/${record.id}`)}
				infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
			/>
		</div>
	);
}
